import calculateContentSpaceRequirement from "./calculateContentSpaceRequirement";
import { FieldData, LeftContentItem, PageData } from "./heightPagination";

/**
 * Calculate the actual space required for content including formatting overhead
 * This should match the calculation in propertyProcessing.ts
 */

export const generatePages = (
  leftColumnContent: LeftContentItem[],
  propertyFieldPages: FieldData[][],
  isLandscape: boolean = false,
): PageData[] => {
  const pages: PageData[] = [];

  // If no left content, just distribute property fields across pages
  if (leftColumnContent.length === 0) {
    propertyFieldPages.forEach((fieldGroup) => {
      pages.push({
        leftContent: [],
        propertyFields: fieldGroup,
        hasExtraInfo: false,
      });
    });
  } else {
    // We have left content - need to coordinate with property fields
    let leftContentIndex = 0;
    let propertyFieldPageIndex = 0;

    while (
      leftContentIndex < leftColumnContent.length ||
      propertyFieldPageIndex < propertyFieldPages.length
    ) {
      const currentPageLeftContent: LeftContentItem[] = [];

      // Add left content to this page (max 2 items per page based on existing logic)
      let itemsAddedToPage = 0;
      while (
        leftContentIndex < leftColumnContent.length &&
        itemsAddedToPage < 2
      ) {
        const item = leftColumnContent[leftContentIndex];

        // Force continued chunks to be on separate pages
        const isContinuedChunk = item.title.includes("(continued)");
        const isStartingNewContinuedChunk =
          isContinuedChunk && currentPageLeftContent.length > 0;

        // Smart amenities placement based on actual space calculation
        const isStartingAmenities = item.type === "amenities";
        let canShareWithExistingContent = false;

        if (isStartingAmenities && currentPageLeftContent.length > 0 && !isContinuedChunk) {
          // Check if this amenities item was smart-chunked to share a page
          const isSmartChunkedAmenities =
            currentPageLeftContent.some(
              (item) =>
                item.type === "comments" && item.title.includes("(continued)"),
            ) && item.title === "Amenities"; // First amenities chunk, not continued

          if (isSmartChunkedAmenities) {
            canShareWithExistingContent = true;
          } else {
            // Calculate actual space usage more accurately using the same method as propertyProcessing.ts
            const existingItems = currentPageLeftContent;
            let currentPageSpaceUsed = 0;

            existingItems.forEach((existingItem, index) => {
              const isFirstItem = index === 0;
              const spaceRequired = calculateContentSpaceRequirement(
                existingItem.content.length,
                isFirstItem, // is first section
              );
              currentPageSpaceUsed += spaceRequired;
            });

            // Use dynamic first page limit based on orientation (should match propertyProcessing.ts)
            const firstPageLimit = isLandscape ? 900 : 1300;
            const remainingSpace = Math.max(
              0,
              firstPageLimit - currentPageSpaceUsed,
            );

            // Calculate space required for the amenities item
            // Use the same overhead calculation as in propertyProcessing (170 chars total)
            const amenitiesSpaceRequired = item.content.length + 170; // Content + header + formatting

            canShareWithExistingContent =
              amenitiesSpaceRequired <= remainingSpace && remainingSpace > 100;
          }
        }

        if (isStartingNewContinuedChunk) {
          // Always start new page for any "(continued)" chunks
          if (currentPageLeftContent.length > 0) {
            break;
          }
        } else if (isStartingAmenities && !canShareWithExistingContent) {
          // Start new page for Amenities if no space available
          if (currentPageLeftContent.length > 0) {
            break;
          }
        }
        currentPageLeftContent.push(item);
        leftContentIndex++;
        itemsAddedToPage++;
      }

      // Get property fields for this page
      const currentPropertyFields =
        propertyFieldPageIndex < propertyFieldPages.length
          ? propertyFieldPages[propertyFieldPageIndex]
          : [];
      if (currentPropertyFields.length > 0) {
        propertyFieldPageIndex++;
      }

      // Create the page
      pages.push({
        leftContent: currentPageLeftContent,
        propertyFields: currentPropertyFields,
        hasExtraInfo: currentPageLeftContent.length > 0,
      });
    }
  }
  return pages;
};
